import { xmlDecl, el, attrs } from './index.js';
import { NS } from '../ns.js';
import type {
  DocumentHead,
  CharProperty,
  ParaProperty,
  FontFace,
  Style,
  BorderFill,
  BorderLine,
  BulletProperty,
  NumberingProperty,
} from '../../../types.js';

export function generateHeaderXml(head: DocumentHead): string {
  return (
    xmlDecl() +
    '\n' +
    el(
      'hh:head',
      {
        'xmlns:hh': NS.hh,
        'xmlns:hc': NS.hc,
        version: '1.4',
        secCnt: '1',
      },
      '\n' +
      generateFontFaces(head.fontFaces) +
      generateBorderFills(head.borderFills) +
      generateCharProperties(head.charProperties) +
      generateParaProperties(head.paraProperties) +
      generateStyles(head.styles) +
      generateNumberings(head.numberingProperties) +
      generateBullets(head.bulletProperties) +
      generateCompatibleDoc(head.compatibleDoc) +
      '',
    )
  );
}

function generateFontFaces(faces: FontFace[]): string {
  if (faces.length === 0) return '';

  const byLang = new Map<string, FontFace[]>();
  for (const f of faces) {
    const arr = byLang.get(f.lang) || [];
    arr.push(f);
    byLang.set(f.lang, arr);
  }

  let xml = '';
  xml += el(
    'hh:fontfaces',
    {},
    '\n' +
    Array.from(byLang.entries())
      .map(([lang, items]) =>
        el(
          'hh:fontface',
          { lang },
          '\n' +
          items
            .map((f, i) =>
              el('hh:font', { id: String(i), face: f.fontName, type: f.fontType || 'ttf' }),
            )
            .join('\n') +
          '\n',
        ),
      )
      .join('\n') +
    '\n',
  );
  xml += '\n';
  return xml;
}

function generateBorderFills(fills: BorderFill[]): string {
  if (fills.length === 0) return '';

  const items = fills.map((bf) => {
    const borderElements = (['left', 'right', 'top', 'bottom'] as const)
      .map((side) => generateBorderLine(side, bf.borders[side]))
      .join('\n');

    let fillContent = '';
    if (bf.fillBrush) {
      if (bf.fillBrush.type === 'SOLID' && bf.fillBrush.faceColor !== undefined) {
        fillContent = el(
          'hc:fillBrush',
          {},
          '\n' +
          el('hc:winBrush', {
            faceColor: colorToString(bf.fillBrush.faceColor),
            hatchColor: bf.fillBrush.patternColor !== undefined ? colorToString(bf.fillBrush.patternColor) : undefined,
          }) +
          '\n',
        );
      }
    }

    return el(
      'hh:borderFill',
      {
        id: String(bf.id),
        threeD: bf.threeD ? '1' : '0',
        shadow: bf.shadow ? '1' : '0',
        slash: bf.slash || 'NONE',
        backSlash: bf.backSlash || 'NONE',
      },
      '\n' + borderElements + '\n' + fillContent,
    );
  });

  return el('hh:borderFills', {}, '\n' + items.join('\n') + '\n') + '\n';
}

function generateBorderLine(side: string, line: BorderLine): string {
  return el(`hc:${side}Border`, {
    type: line.type,
    width: line.width,
    color: colorToString(line.color),
  });
}

function generateCharProperties(props: CharProperty[]): string {
  if (props.length === 0) return '';

  const items = props.map((cp) => {
    const fontRefs =
      el('hh:fontRef', {
        hangul: String(cp.fontRef.hangul),
        latin: String(cp.fontRef.latin),
        hanja: String(cp.fontRef.hanja),
        japanese: String(cp.fontRef.japanese),
        other: String(cp.fontRef.other),
        symbol: String(cp.fontRef.symbol),
        user: String(cp.fontRef.user),
      });

    return el(
      'hh:charPr',
      {
        id: String(cp.id),
        height: String(cp.height),
        textColor: colorToString(cp.textColor),
        shadeColor: cp.shadeColor !== undefined ? colorToString(cp.shadeColor) : undefined,
        useFontSpace: cp.useFontSpace ? '1' : undefined,
        useKerning: cp.useKerning ? '1' : undefined,
        spacing: cp.spacing !== undefined ? String(cp.spacing) : undefined,
        relSz: cp.relSize !== undefined ? String(cp.relSize) : undefined,
        offset: cp.charOffset !== undefined ? String(cp.charOffset) : undefined,
        bold: cp.bold ? '1' : '0',
        italic: cp.italic ? '1' : '0',
        underline: cp.underline || 'NONE',
        strikeout: cp.strikeout || 'NONE',
      },
      '\n' + fontRefs + '\n',
    );
  });

  return el('hh:charProperties', {}, '\n' + items.join('\n') + '\n') + '\n';
}

function generateParaProperties(props: ParaProperty[]): string {
  if (props.length === 0) return '';

  const items = props.map((pp) => {
    const marginEl = el('hh:parMargin', {
      left: String(pp.paraMargin.left),
      right: String(pp.paraMargin.right),
      indent: String(pp.paraMargin.indent),
      prev: String(pp.paraMargin.prevSpacing),
      next: String(pp.paraMargin.nextSpacing),
    });

    const lineSpacingEl = el('hh:lineSpacing', {
      type: pp.lineSpacing.type,
      value: String(pp.lineSpacing.value),
    });

    return el(
      'hh:paraPr',
      {
        id: String(pp.id),
        align: pp.alignment || 'JUSTIFY',
        heading: pp.heading,
        breakBefore: pp.breakBefore,
      },
      '\n' + marginEl + '\n' + lineSpacingEl + '\n',
    );
  });

  return el('hh:paraProperties', {}, '\n' + items.join('\n') + '\n') + '\n';
}

function generateStyles(styles: Style[]): string {
  if (styles.length === 0) return '';

  const items = styles.map((s) =>
    el('hh:style', {
      id: String(s.id),
      type: s.type,
      name: s.name,
      engName: s.engName,
      paraPrIDRef: s.paraPrIDRef !== undefined ? String(s.paraPrIDRef) : undefined,
      charPrIDRef: s.charPrIDRef !== undefined ? String(s.charPrIDRef) : undefined,
      nextStyleIDRef: s.nextStyleIDRef !== undefined ? String(s.nextStyleIDRef) : undefined,
    }),
  );

  return el('hh:styles', {}, '\n' + items.join('\n') + '\n') + '\n';
}

function generateNumberings(numberings: NumberingProperty[]): string {
  if (numberings.length === 0) return '';

  const items = numberings.map((n) => {
    const levels = n.levels
      .map((lvl, i) =>
        el('hh:paraHead', {
          level: String(i + 1),
          numFormat: lvl.format,
          start: lvl.start !== undefined ? String(lvl.start) : '1',
          prefix: lvl.prefix || '',
          suffix: lvl.suffix || '.',
        }),
      )
      .join('\n');

    return el('hh:numbering', { id: String(n.id) }, '\n' + levels + '\n');
  });

  return el('hh:numberings', {}, '\n' + items.join('\n') + '\n') + '\n';
}

function generateBullets(bullets: BulletProperty[]): string {
  if (bullets.length === 0) return '';

  const items = bullets.map((b) =>
    el('hh:bullet', {
      id: String(b.id),
      char: b.bulletChar,
      bulletSz: b.bulletSize !== undefined ? String(b.bulletSize) : undefined,
    }),
  );

  return el('hh:bullets', {}, '\n' + items.join('\n') + '\n') + '\n';
}

function generateCompatibleDoc(compat?: string): string {
  if (!compat) return '';
  return el('hh:compatibleDocument', { targetProgram: compat }) + '\n';
}

function colorToString(color: number): string {
  return '#' + color.toString(16).padStart(6, '0');
}
